if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        this.openedBy = opener;
        opener.setAttribute('aria-disabled', 'true');
        opener.classList.add('loading');

        const spinner = opener.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');

        // Open modal immediately so users get instant feedback while product HTML loads.
        this.modalContent.innerHTML = `
          <div class="quick-add-modal__loading" role="status" aria-live="polite">
            <div class="loading__spinner">
              <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
                <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
              </svg>
            </div>
          </div>
        `;
        super.show(opener);

        const productUrl = opener.getAttribute('data-product-url');

        fetch(productUrl)
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            if (!productElement) {
              throw new Error('Quick add product content not found');
            }

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);
            this.ensureMediaVisible();

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            this.modalContent.querySelector('product-component')?.dispatchViewEvent?.();

            trapFocus(this, this.querySelector('[role="dialog"]'));
          })
          .catch(() => {
            this.hide(true);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            if (spinner) spinner.classList.add('hidden');
          });
      }

      ensureMediaVisible() {
        const root = this.modalContent.querySelector('product-info.quick-add-product');
        if (!root) return;

        const mediaItems = root.querySelectorAll('.product__media-item');
        if (!mediaItems.length) return;

        mediaItems.forEach((item, index) => {
          item.classList.remove('scroll-trigger--offscreen');
          item.classList.add('scroll-trigger--cancel');
          if (index === 0) item.classList.add('is-active');
        });

        root.querySelectorAll('.product__media img').forEach((img) => {
          img.loading = 'eager';
          img.removeAttribute('loading');
        });
      }

      /**
       * Only mutate product-info trees prepared for this modal.
       * Never register this as a product-info preProcess callback — that can run on
       * variant-selects fragments and damage PDP HTML.
       */
      preprocessHTML(productElement) {
        if (!(productElement instanceof HTMLElement) || productElement.tagName !== 'PRODUCT-INFO') return;

        productElement.classList.add('quick-add-product');

        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient') {
            this.modalContent.classList.add(classApplied);
          }
        });

        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        productElement.querySelector('pickup-availability')?.remove();
        productElement.querySelector('product-modal')?.remove();
        productElement.querySelectorAll('modal-dialog').forEach((modal) => modal.remove());
        productElement.querySelectorAll('.product-layouts').forEach((el) => el.remove());
        // Strip PDP vertical-thumbnail chrome so modal uses Dawn's simple 2-col layout
        productElement.querySelectorAll('.thumbnail-slider').forEach((el) => el.remove());
        productElement.querySelectorAll('.product__title--mobile').forEach((el) => el.remove());
        const product = productElement.querySelector('.product');
        if (product) {
          product.classList.remove('product--thumbnail', 'product--thumbnail_slider');
        }
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;
        if (!sectionId) return;

        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(sectionId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(sectionId)) {
            productElement.setAttribute(attribute.name, attribute.value.replaceAll(sectionId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        if (!product?.classList.contains('product--columns')) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
